// React Components
import React, { Fragment, useEffect, useMemo, useReducer, useRef } from 'react';
import { withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

// Ant Design Components
import {
  Drawer, Tabs, Badge,
} from 'antd';

// Utilities & Helpers
import { toISTDate } from '@Apis/constants';
import Helpers from '@Apis/helpers';
import CustomFieldHelpers from '@Helpers/CustomFieldHelpers';
import { getLinkedDocumentInfo, getQcData, handleFileChange, handleFileChangeEWayBill } from './helpers';

// UI Components
import H3Text from '@Uilib/h3Text';
import H3Image from '@Uilib/h3Image';

// Images
import closeIcon from '@Images/icons/icon-close-blue.png';

// Actions
import GRNActions from '@Actions/grnActions';
import PaymentOutgoingActions from '@Actions/paymentOutgoingActions';
import AttachmentActions from '@Actions/attachmentActions';
import ActivityLogActions from '@Actions/activityLogActions';
import ZohoIntegrationActions from '@Actions/integrations/zohoIntegrationActions';
import TallyIntegrationActions from '@Actions/integrations/tallyIntegrationActions';
import QualityCheckActions from '@Actions/quality/qualityCheckActions';
import AnalyticsActions from '@Actions/application/analyticsActions';
import BusyIntegrationActions from '../../../../../actions/integrations/busyIntegrationAction';

// Common Components
import PRZModal from '../../../../Common/UI/PRZModal';
import ViewPayment from '@Components/Common/ViewPayment';
import VendorRating from '../../../../Common/VendorRating';
import ViewLoadingSkull from '../../../../Common/ViewLoadingSkull';
import ViewDocRightSection from '../../../../Common/ViewDocRightSection';

// GRN & Related
import ViewGRNHeader from './ViewGRNHeader';
import ViewGRNFooter from './ViewGRNFooter';
import ViewGRNLines from './ViewGRNLines';
import ViewGRNDetails from './ViewGRNDetails';
import QualityCheckGRN from './QualityCheckGRN';

// Purchase Components
import ViewDebitNote from '@Components/Purchase/DebitNotes/ViewDebitNote';

// Styles
import './style.scss';

// Reducer
import { actionType, reducer } from './reducers';

const { TabPane } = Tabs;
const ViewGRN = ({
  selectedGRN, createGRNLoading, updateGRNStatus, callback, updateGRNStatusLoading, deleteGrnLoading, deleteGrn, pushGrnToBusy, pushGrnToBusyLoading, user, getGRNByIdLoading,
  getGRNById, selectedGrnId, getPaymentsOutgoingSuccess, syncGRN, pullTallyVendorPayment, pullTallyVendorPaymentLoading, syncTallyGRN, syncTallyGRNLoading,
  match, isQuickView, history, destinationTenantId, getAttachmentByIdLoading, updateAttachmentLoading, syncGRNLoading, updateGRNWorkflowStep, updateGRNWorkflowStepLoading,
  MONEY, quickUpdateGRN, quickUpdateGRNLoading, getDownloadQualityChecks, downloadDocument, getActivityLog, showGrnAndLabelDropDown, priceMasking, updateAttachment,
  getAttachmentById, createAndLinkPaymentLoading, unLinkPaymentLoading, selectedAttachment, addGRN, createAndLinkPayment, unLinkPayment, getGRNByIdSuccess,
  getActivityLogSuccess,
}) => {
  const initialState = useMemo(() => {
    return {
      currentTab: '/goods-received',
      isGetAttachment: false,
      showModal: false,
      isUpdateRating: false,
      isLabelAvailable: false,
      eWayBillAttachment: [],
      visibleColumns: {
        SKU: {
          label: 'SKU#',
          visible: true,
          disabled: true,
        },
        RECEIVED: {
          label: 'Received',
          visible: true,
          disabled: true,
        },
        INVOICE_QUANTITY: {
          label: 'Invoice Quantity',
          visible: true,
          disabled: true,
        },
        UNIT_PRICE: {
          label: 'Unit Price',
          visible: true,
          disabled: true,
        },
        DISCOUNT: {
          label: 'Discount',
          visible: true,
          disabled: true,
        },
        TOTAL: {
          label: 'Total',
          visible: true,
          disabled: true,
        },
      },
      isUserReadyForPrefillData: false,
      fileList: [],
      selectedTags: [],
      updatedGstNumber: '',
      updateInvoiceNumber: '',
      updateEWayBillNumber: '',
      updateInvoiceDate: null,
      updateDueDate: null,
      cfGRNLine: [],
      isApInvoiceEnabled: false,
      showPRZModal: false,
      selectedDocumentId: null,
      selectedRows: [],
      selectedRowKeys: [],
      showViewPayout: false,
      selectedPayment: null,
      openDNModalView: false,
    };
  }, []);

  const [state, dispatch] = useReducer(reducer, initialState);

  const {
    updateInvoiceDate, updateDueDate, updatedGstNumber, openDNModalView, selectedDebitNote, showViewPayout, selectedPayment, fileList, currentTab, updateEWayBillNumber, eWayBillAttachment,
    cfGRNLine, selectedRowKeys, selectedRows, showModal, isUpdateRating, selectedTags, updateInvoiceNumber, isLabelAvailable, showPRZModal, selectedDocumentId, isApInvoiceEnabled,
    visibleColumns, isGetAttachment, isUserReadyForPrefillData,
  } = state;

  const fileInputRef = useRef();

  const setState = (values) => {
    dispatch({
      type: actionType.SET_MULTIPLE_FIELDS,
      payload: values,
    });
  };

  useEffect(() => {
    getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);

    return () => {
      getGRNByIdSuccess(null);
    };
  }, []);

  useEffect(() => {
    if (selectedGRN && !isUserReadyForPrefillData) {
      const lineCf = selectedGRN?.grn_lines?.[0]?.grn_line_custom_fields;
      setState({
        fileList: selectedGRN?.attachments,
        selectedTags: selectedGRN?.tags,
        isUserReadyForPrefillData: true,
        updatedGstNumber: selectedGRN?.tenant_seller_info?.gst_number,
        updateInvoiceNumber: selectedGRN?.invoice_number,
        updateEWayBillNumber: selectedGRN?.e_way_bill_number,
        updateInvoiceDate: selectedGRN?.invoice_date ? (toISTDate(selectedGRN?.invoice_date)) : null,
        updateDueDate: selectedGRN?.invoice_date ? (toISTDate(selectedGRN?.grn_due_date)) : null,
        cfGRNLine: lineCf,
        visibleColumns: CustomFieldHelpers.updateVisibleColumns(lineCf, state.visibleColumns),
        isApInvoiceEnabled: selectedGRN?.allow_grn_to_create_ap_invoice,
      });
    }

  }, [selectedGRN, isUserReadyForPrefillData]);

  useEffect(() => {
    if (selectedAttachment && isGetAttachment && !getAttachmentByIdLoading && !updateAttachmentLoading) {
      setState({
        fileList: selectedAttachment,
        isGetAttachment: false,
      });
    }
  }, [selectedAttachment, isGetAttachment, getAttachmentByIdLoading, updateAttachmentLoading]);

  const fixedMenuBar = useMemo(() => user?.side_menu_bar_type === 'FIXED', [user]);

  return (
    <Fragment>
      {(getGRNByIdLoading === false && !pushGrnToBusyLoading) ? (
        <div className={`view-document__wrapper ${!isQuickView ? 'view-document__wrapper-page' : ''}`}>
          <div className="ant-row">
            <div className={`${fixedMenuBar ? 'ant-col-md-16' : 'ant-col-md-17'} ant-col-xs-24`}>
              <div className={`view-left__wrapper ${!isQuickView ? 'is-page-view' : ''}`}>
                <ViewGRNHeader
                  selectedGRN={selectedGRN}
                  destinationTenantId={destinationTenantId}
                  user={user}
                  downloadDocument={downloadDocument}
                  isLabelAvailable={isLabelAvailable}
                  setState={(stateValue) => setState(stateValue)}
                  isQuickView={isQuickView}
                  updateGRNStatus={updateGRNStatus}
                  getGRNById={getGRNById}
                  selectedGrnId={selectedGrnId}
                  callback={callback}
                  match={match}
                  updateGRNStatusLoading={updateGRNStatusLoading}
                  deleteGrn={deleteGrn}
                  deleteGrnLoading={deleteGrnLoading}
                  showGrnAndLabelDropDown={showGrnAndLabelDropDown}
                  selectedRows={selectedRows}
                  isApInvoiceEnabled={isApInvoiceEnabled}
                  syncGRN={syncGRN}
                  syncGRNLoading={syncGRNLoading}
                  getDownloadQualityChecks={getDownloadQualityChecks}
                  syncTallyGRN={syncTallyGRN}
                  syncTallyGRNLoading={syncTallyGRNLoading}
                  pullTallyVendorPayment={pullTallyVendorPayment}
                  pushGrnToBusy={pushGrnToBusy}
                  updatedGstNumber={updatedGstNumber}
                  getPaymentsOutgoingSuccess={getPaymentsOutgoingSuccess}
                  history={history}
                />

                <ViewGRNDetails
                  selectedGRN={selectedGRN}
                  user={user}
                  updateInvoiceDate={updateInvoiceDate}
                  quickUpdateGRN={quickUpdateGRN}
                  quickUpdateGRNLoading={quickUpdateGRNLoading}
                  updatedGstNumber={updatedGstNumber}
                  updateInvoiceNumber={updateInvoiceNumber}
                  updateEWayBillNumber={updateEWayBillNumber}
                  getActivityLog={getActivityLog}
                  updateDueDate={updateDueDate}
                  eWayBillAttachment={eWayBillAttachment}
                  isApInvoiceEnabled={isApInvoiceEnabled}
                  getGRNById={getGRNById}
                  selectedGrnId={selectedGrnId}
                  match={match}
                  setState={(stateValue) => setState(stateValue)}
                  fileInputRef={fileInputRef}
                  handleFileChangeEWayBill={(event) => handleFileChangeEWayBill({
                    event,
                    setState: (stateValue) => setState(stateValue),
                  })}
                />

                <br />

                <Tabs
                  activeKey={currentTab}
                  onChange={(key) => {
                    setState({ currentTab: key });
                  }}
                  mode="horizontal"
                  type="card"
                >
                  <TabPane tab="Goods Received" key="/goods-received" />
                  {getQcData({ data: selectedGRN?.grn_lines, selectedGRN })?.length > 0 && Helpers.getPermission(Helpers.permissionEntities.QUALITY_CHECKS, Helpers.permissionTypes.READ, user) && (
                    <TabPane
                      tab={(
                        <div>
                          Quality Checks &nbsp;
                          {getQcData({ data: selectedGRN?.grn_lines, selectedGRN })?.filter((item) => item.status !== 'COMPLETED')?.length ? <Badge count={getQcData({ data: selectedGRN?.grn_lines, selectedGRN })?.filter((item) => item.status !== 'COMPLETED')?.length} /> : ''}
                        </div>
                      )}
                      key="/grn-quality-checks"
                    />
                  )}
                </Tabs>
                {currentTab === '/goods-received' && (
                  <Fragment>
                    <ViewGRNLines
                      selectedGRN={selectedGRN}
                      loading={createGRNLoading || syncGRNLoading || pullTallyVendorPaymentLoading || syncTallyGRNLoading}
                      user={user}
                      MONEY={MONEY}
                      selectedRowKeys={selectedRowKeys}
                      setState={(stateValue) => setState(stateValue)}
                      priceMasking={priceMasking}
                      isApInvoiceEnabled={isApInvoiceEnabled}
                      visibleColumns={visibleColumns}
                      cfGRNLine={cfGRNLine}
                    />

                    <ViewGRNFooter
                      selectedGRN={selectedGRN}
                      user={user}
                      MONEY={MONEY}
                      fileList={fileList}
                      getAttachmentByIdLoading={getAttachmentByIdLoading}
                      updateAttachmentLoading={updateAttachmentLoading}
                      handleFileChange={(value) => handleFileChange({
                        fileListData: value,
                        selectedGRN,
                        updateAttachment,
                        getAttachmentById,
                        setState: (stateValue) => setState(stateValue),
                      })}
                      priceMasking={priceMasking}
                      isApInvoiceEnabled={isApInvoiceEnabled}
                    />
                  </Fragment>
                )}
                {currentTab === '/grn-quality-checks' && (
                  <React.Fragment>
                    <QualityCheckGRN
                      callback={() => {
                        if (callback) callback();
                        getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                      }}
                      selectedGRNLines={getQcData({ data: selectedGRN?.grn_lines, selectedGRN })}
                    />
                  </React.Fragment>
                )}
              </div>
            </div>
            <ViewDocRightSection
              entityType="GOOD_RECEIVING_NOTE"
              entityId={selectedGRN?.grn_id}
              MONEY={MONEY}
              user={user}
              isQuickView={isQuickView}
              tagSelectorProps={{
                isEnabled: true,
                entityType: 'GRN',
                selectedTags,
                isMultipleTagsAllowed: true,
                tagSearchAllowed: true,
                onChange: (value) => setState({ selectedTags: value }),
                placeholder: 'Select Tags',
              }}
              workflowTimelineProps={{
                isEnabled: selectedGRN?.status !== 'DRAFT',
                workflowSteps: selectedGRN?.workflow_steps || {},
                workflowStepId: selectedGRN?.workflow_step_id,
                loading: updateGRNWorkflowStepLoading,
                updateStep: updateGRNWorkflowStep,
                callback: () => {
                  getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
                },
              }}
              activityLogProps={{
                isEnabled: selectedGRN,
                entityName: 'Good Receiving Note',
                entityType: 'good_receiving_note',
              }}
              linkedDocumentsInfo={getLinkedDocumentInfo({
                selectedGRN,
                user,
                priceMasking,
                showViewPayout,
                setState: (values) => setState(values),
              })}
            />
          </div>
        </div>
      ) : (
        // Loader
        <ViewLoadingSkull isQuickView={isQuickView} fixedMenuBar={fixedMenuBar} />
      )
      }
      <Drawer
        onClose={() => setState({ openDNModalView: false })}
        open={openDNModalView}
        width="820px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '775px' }}>
            <H3Text text="View Debit Note" className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setState({ openDNModalView: false })} />
          </div>
        </div>
        <div style={{ marginTop: '-35px' }}>
          <ViewDebitNote
            selectedDNId={selectedDebitNote?.dn_id}
            callback={() => {
              setState({ openDNModalView: false });
              getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
            }}
            isQuickView
          />
        </div>
      </Drawer>
      <Drawer
        open={showViewPayout}
        onClose={() => setState({ showViewPayout: false })}
        width="1000px"
        destroyOnClose
      >
        <div className="custom-drawer__header-wrapper">
          <div className="custom-drawer__header" style={{ width: '950px' }}>
            <H3Text text={`View Payment #${selectedPayment?.debit_note_number || selectedPayment?.payment_id}`} className="custom-drawer__title" />
            <H3Image src={closeIcon} className="custom-drawer__close-icon" onClick={() => setState({ showViewPayout: false })} />
          </div>
        </div>
        <ViewPayment
          selectedPayment={selectedPayment}
          selectedPaymentId={selectedPayment?.payment_id}
          paymentType={selectedPayment?.applied_payment_type}
          callback={() => {
            setState({ showViewPayout: false });
            getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
          }}
        />
      </Drawer>
      <VendorRating
        selectedEntity={selectedGRN}
        selectedSeller={selectedGRN?.tenant_seller_info}
        openModal={showModal}
        isUpdateRating={isUpdateRating}
        closeModal={() => setState({ showModal: false })}
        updateCallback={() => {
          setState({ showModal: false });
          getGRNById(Helpers.getTenantEntityPermission(user?.user_tenants, Helpers.permissionEntities.GOOD_RECEIVING, Helpers.permissionTypes.READ).join(','), selectedGrnId || match?.params?.grnId);
        }}
      />
      <PRZModal
        isOpen={showPRZModal}
        onClose={() => {
          setState({ showPRZModal: false, selectedDocumentId: null });
        }}
        entityType="product"
        documentId={selectedDocumentId}
      />
    </Fragment >

  );

};

const mapStateToProps = ({
  UserReducers, GRNReducers, PaymentOutgoingReducers, AttachmentReducers, ZohoIntegrationReducers, TallyIntegrationReducers, BusyIntegrationReducers,
}) => ({
  user: UserReducers.user,
  createGRNLoading: GRNReducers.createGRNLoading,
  MONEY: UserReducers.MONEY,
  updateGRNStatusLoading: GRNReducers.updateGRNStatusLoading,
  createAndLinkPaymentLoading: PaymentOutgoingReducers.createAndLinkPaymentLoading,
  getGRNByIdLoading: GRNReducers.getGRNByIdLoading,
  selectedGRN: GRNReducers.selectedGRN,
  deleteGrnLoading: GRNReducers.deleteGrnLoading,
  updateGRNWorkflowStepLoading: GRNReducers?.updateGRNWorkflowStepLoading,
  unLinkPaymentLoading: PaymentOutgoingReducers.unLinkPaymentLoading,
  selectedAttachment: AttachmentReducers.selectedAttachment,
  getAttachmentByIdLoading: AttachmentReducers.getAttachmentByIdLoading,
  updateAttachmentLoading: AttachmentReducers.updateAttachmentLoading,
  syncGRNLoading: ZohoIntegrationReducers.syncGRNLoading,
  syncTallyGRNLoading: TallyIntegrationReducers.syncTallyGRNLoading,
  pullTallyVendorPaymentLoading: TallyIntegrationReducers.pullTallyVendorPaymentLoading,
  pushGrnToBusyLoading: BusyIntegrationReducers.pushGrnToBusyLoading,
  quickUpdateGRNLoading: GRNReducers.quickUpdateGRNLoading,
  priceMasking: UserReducers.priceMasking,
});

const mapDispatchToProps = (dispatch) => ({
  getGRNById: (tenantId, grnId) => dispatch(GRNActions.getGRNById(tenantId, grnId)),
  addGRN: (payload, action) => dispatch(GRNActions.createGRN(payload, action)),
  updateGRNStatus: (payload, action) => dispatch(GRNActions.updateGRNStatus(payload, action)),
  deleteGrn: (tenantId, grnId, callback) => dispatch(GRNActions.deleteGrn(tenantId, grnId, callback)),
  createAndLinkPayment: (payload, callback) => dispatch(PaymentOutgoingActions.createAndLinkPayment(payload, callback)),
  unLinkPayment: (grnPaymentId, callback) => dispatch(PaymentOutgoingActions.unLinkPayment(grnPaymentId, callback)),
  getPaymentsOutgoingSuccess: (payments) => dispatch(PaymentOutgoingActions.getPaymentsOutgoingSuccess(payments)),
  getAttachmentById: (entityId, entityName, callback) => dispatch(
    AttachmentActions.getAttachmentById(entityId, entityName, callback),
  ),
  updateAttachment: (payload, callback) => dispatch(
    AttachmentActions.updateAttachment(payload, callback),
  ),
  getGRNByIdSuccess: (grnData) => dispatch(GRNActions.getGRNByIdSuccess(grnData)),
  syncGRN: (payload, callback) => dispatch(ZohoIntegrationActions.syncGRN(payload, callback)),
  syncTallyGRN: (payload, callback) => dispatch(TallyIntegrationActions.syncTallyGRN(payload, callback)),
  pullTallyVendorPayment: (payload, callback) => dispatch(TallyIntegrationActions.pullTallyVendorPayment(payload, callback)),
  getActivityLogSuccess: (activityLog) => dispatch(ActivityLogActions.getActivityLogSuccess(activityLog)),
  updateGRNWorkflowStep: (payload, callback) => dispatch(GRNActions.updateGRNWorkflowStep(payload, callback)),
  pushGrnToBusy: (payload, callback) => dispatch(BusyIntegrationActions.pushGrnToBusy(payload, callback)),
  quickUpdateGRN: (payload, callback, rollback) => dispatch(GRNActions.quickUpdateGRN(payload, callback, rollback)),
  getDownloadQualityChecks: (tenantId, qualityCheckId, entityName, entityId) => dispatch(QualityCheckActions.getDownloadQualityChecks(tenantId, qualityCheckId, entityName, entityId)),
  downloadDocument: (payload, document) => dispatch(AnalyticsActions.downloadDocument(payload, document)),
  getActivityLog: (entityId, entityName, page, limit, callback) => dispatch(ActivityLogActions.getActivityLog(entityId, entityName, page, limit, callback)),
});

export default connect(mapStateToProps, mapDispatchToProps)(withRouter(ViewGRN));
